import { createClient } from '@supabase/supabase-js';

/**
 * Widget API Service
 * Fetches catalog data for embeddable widget
 */

const toCamelCase = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toCamelCase);

  return Object.keys(obj)?.reduce((acc, key) => {
    const camelKey = key?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
    acc[camelKey] = typeof obj?.[key] === 'object' ? toCamelCase(obj?.[key]) : obj?.[key];
    return acc;
  }, {});
};

export const widgetApiService = {
  /**
   * Get catalog with pages and hotspots
   * @param {string} catalogId - Catalog ID
   * @param {string} apiUrl - Supabase URL
   * @param {string} apiKey - Supabase anon key
   * @returns {Promise<Object>} Catalog data
   */
  async getCatalog(catalogId, apiUrl, apiKey) {
    try {
      const supabase = createClient(apiUrl, apiKey);

      // Fetch catalog with pages and hotspots
      const { data, error } = await supabase
        ?.from('catalogs')
        ?.select(`
          *,
          company:companies(vtex_account_name),
          catalog_pages(
            id,
            page_number,
            image_url,
            image_alt,
            thumbnail_url,
            thumbnail_alt
          ),
          hotspots(
            id,
            page_number,
            x,
            y,
            width,
            height,
            type,
            title,
            description,
            sku,
            url,
            price,
            quantity,
            seller,
            vtex_store_url,
            visible
          )
        `)
        ?.eq('id', catalogId)
        ?.eq('status', 'published')
        ?.single();

      if (error) throw error;

      // Transform data to camelCase
      const catalog = toCamelCase(data);

      // Sort pages by page number
      if (catalog?.catalogPages) {
        catalog.pages = catalog?.catalogPages?.sort((a, b) => a?.pageNumber - b?.pageNumber);
        delete catalog?.catalogPages;
      }

      // Group hotspots by page
      if (catalog?.hotspots && catalog?.pages) {
        catalog.pages = catalog?.pages?.map((page) => ({
          ...page,
          hotspots: catalog?.hotspots
            ?.filter((h) => h?.pageNumber === page?.pageNumber && h?.visible)
            ?.sort((a, b) => a?.y - b?.y)
        }));
        delete catalog?.hotspots;
      }

      // Increment view count
      await supabase
        ?.from('catalogs')
        ?.update({ views: (catalog?.views || 0) + 1 })
        ?.eq('id', catalogId);

      return catalog;
    } catch (error) {
      console.error('Error fetching catalog:', error);
      throw error;
    }
  },

  /**
   * Increment hotspot clicks
   * @param {string} catalogId - Catalog ID
   * @param {string} apiUrl - Supabase URL
   * @param {string} apiKey - Supabase anon key
   */
  async incrementHotspotClicks(catalogId, apiUrl, apiKey) {
    try {
      const supabase = createClient(apiUrl, apiKey);

      const { data: catalog } = await supabase
        ?.from('catalogs')
        ?.select('hotspot_clicks')
        ?.eq('id', catalogId)
        ?.single();

      await supabase
        ?.from('catalogs')
        ?.update({ hotspot_clicks: (catalog?.hotspot_clicks || 0) + 1 })
        ?.eq('id', catalogId);
    } catch (error) {
      console.error('Error incrementing hotspot clicks:', error);
    }
  }
};